"use client";

import React from "react";
import { CircleCheck as CheckCircle2, FileSearch, Cpu, ShieldCheck, Send, Loader as Loader2, Check } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface ChecklistItem {
  id: number;
  title: string;
  description: string;
  iconName: "FileSearch" | "Cpu" | "ShieldCheck";
}
const checklistItems: ChecklistItem[] = [{
  id: 1,
  title: "Spectral Data Review",
  description: "Comprehensive feasibility assessment of NIR, Raman, or powder sample spectra with signal-to-noise evaluation.",
  iconName: "FileSearch"
}, {
  id: 2,
  title: "Model Architecture Recommendation",
  description: "Tailored chemometric models, hardware selection, and integration strategy for process analytics.",
  iconName: "Cpu"
}, {
  id: 3,
  title: "Compliance Scoping",
  description: "Alignment with GMP, 21 CFR Part 11, and site-specific validation protocols across UK and Irish facilities.",
  iconName: "ShieldCheck"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-lg border border-border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Check className="size-6" />
        </div>
        <h3 className="text-xl font-semibold text-card-foreground">Request Received</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {successMessage || "Thank you. An application specialist will review your project requirements and be in touch shortly."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Please enter at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-xs font-semibold uppercase tracking-wider">
                Full Name *
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-xs font-semibold uppercase tracking-wider">
                Work Email *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-xs font-semibold uppercase tracking-wider">
                Company / Site Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" className="bg-background text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-xs font-semibold uppercase tracking-wider">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" className="bg-background text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please provide details about your project or application",
      minLength: {
        value: 10,
        message: "Please enter at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-xs font-semibold uppercase tracking-wider">
              Project Scope Notes *
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your analytical targets, spectroscopic modality (NIR/Raman/Powder), timeline, and site constraints..." className="min-h-32 bg-background text-foreground resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full mt-2">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-4" />
            Sending Details...
          </> : <>
            <Send className="mr-2 size-4" />
            Submit Consultation Request
          </>}
      </Button>
    </Form>;
}
export default function ConsultationContact() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationcontact" data-nav="light" className="relative bg-background py-16 sm:py-24">
      <div className="page-container">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-start">
          {/* Left Column (5 cols): Consultation Checklist */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? false : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                <CheckCircle2 className="size-3.5 text-primary" />
                <span>Application Engineering</span>
              </div>
              
              <h2 className="mt-4 font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                Technical Consultation & Application Scoping
              </h2>
              
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Partner directly with our senior analytical specialists to evaluate your method requirements, test instrument compatibility, and outline compliant implementation paths for regulated environments.
              </p>
            </div>

            {/* Checklist items */}
            <div className="mt-8 space-y-4">
              {checklistItems.map((item, i) => <div key={item.id} data-index={i} className="group flex gap-4 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40 shadow-xs">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    {item.iconName === "FileSearch" && <FileSearch className="size-5" />}
                    {item.iconName === "Cpu" && <Cpu className="size-5" />}
                    {item.iconName === "ShieldCheck" && <ShieldCheck className="size-5" />}
                  </div>
                  <div>
                    <h3 className="font-default text-base font-semibold text-card-foreground">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-sm leading-normal text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 rounded-lg border border-border bg-secondary/50 p-4 text-xs text-muted-foreground">
              <p>
                Trusted across pharmaceutical, chemical, and regulated manufacturing sites throughout Ireland and the United Kingdom.
              </p>
            </div>
          </motion.div>

          {/* Right Column (7 cols): Embedded Contact Form */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? false : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <Card className="border border-border bg-card shadow-md">
              <CardContent className="p-6 sm:p-8">
                <div className="mb-6">
                  <h3 className="font-default text-2xl font-bold text-card-foreground">
                    Contact Us - Antech Solutions
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Submit your application details below and our team will get back to you within one business day.
                  </p>
                </div>

                <FormProvider formId="contact_form" sectionName="consultation-contact" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                company_name: {},
                phone_number: {},
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Submit Consultation Request" successMessage="Thank you! We'll review your application and get back to you soon." errorMessage="Unable to submit your request. Please check all fields and try again.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}